/**
 * Mode-aware route-definities. Twee shells:
 *
 *  - `superadmin` — root op admin.<central>; views in src/apps/superadmin
 *  - `tenant` — root op {slug}.<central>; views in src/apps/tenant
 *
 * Lazy imports zodat de bundle gesplitst kan worden per shell. Routes
 * binnen de auth-gated outlet zitten in een `<RequireAuth>`-wrapper
 * via de element-tree (niet via route-meta) — composition over
 * configuration, idiomatic React Router v6.
 *
 * Alle M9/M10 views zijn nu in React; geen placeholders meer.
 */
import { lazy, Suspense, type ReactNode } from 'react';
import { Routes, Route, Navigate, useLocation, useParams } from 'react-router-dom';

import { AppShell } from '@shared/components/AppShell';
import { GuestOnly, RequireAdminConsole, RequireAuth, RequireGrader, RequirePermission } from './guards';
import { SETTINGS_ATOMS, SETTINGS_MODULES, settingsModuleAtom } from '@shared/components/settingsPages';
import { useAppMode } from '@shared/app-mode-context';
import { useAuthStore } from '@shared/stores/auth';
import { useTenantMeta } from '@shared/hooks/useTenantMeta';
import { useCatalogMenu } from '@shared/hooks/useCatalogs';
import { SurfaceProvider } from '@shared/questionnaireSurface';

// Lazy view imports — werken pas zodra de view-bestanden bestaan.
// Voor scaffold-validatie gebruiken we placeholders.
const SuperadminLogin = lazy(() => import('@/apps/superadmin/views/LoginView'));
const SuperadminDashboard = lazy(() => import('@/apps/superadmin/views/DashboardView'));
const SuperadminTenantsIndex = lazy(() => import('@/apps/superadmin/views/TenantsIndexView'));
const SuperadminTenantSetup = lazy(() => import('@/apps/superadmin/views/TenantSetupView'));
const SuperadminTenantPlatformSettings = lazy(() => import('@/apps/superadmin/views/TenantPlatformSettingsView'));
const SuperadminOrgStructure = lazy(() => import('@/apps/superadmin/views/OrgStructureView'));
const SuperadminAuditLog = lazy(() => import('@/apps/superadmin/views/AuditLogView'));
const SuperadminPlatformAssets = lazy(() => import('@/apps/superadmin/views/PlatformAssetsView'));
const SuperadminPlatformLocales = lazy(() => import('@/apps/superadmin/views/PlatformLocalesView'));
const SuperadminPlatformSettings = lazy(() => import('@/apps/superadmin/views/PlatformSettingsView'));
const SuperadminMailOutbox = lazy(() => import('@/apps/superadmin/views/CentralMailOutboxView'));
const SuperadminMailTemplates = lazy(() => import('@/apps/superadmin/views/CentralMailTemplatesView'));
const SuperadminMailTemplateEditor = lazy(() => import('@/apps/superadmin/views/CentralMailTemplateEditorView'));
const Superadmins = lazy(() => import('@/apps/superadmin/views/SuperadminsView'));
const SuperadminPlatformWorkflows = lazy(() => import('@/apps/superadmin/views/PlatformWorkflowsIndexView'));
const SuperadminPlatformWorkflowEditor = lazy(() => import('@/apps/superadmin/views/PlatformWorkflowEditorView'));
const SuperadminImportsOverview = lazy(() => import('@/apps/superadmin/views/ImportsOverviewView'));
const SuperadminImportRunDetail = lazy(() => import('@/apps/superadmin/views/ImportRunDetailView'));
const SuperadminImportSourceRun = lazy(() => import('@/apps/superadmin/views/ImportSourceRunView'));
const SuperadminImportSourceWizard = lazy(() => import('@/apps/superadmin/views/ImportSourceWizardView'));
const TenantAttendanceOccurrences = lazy(
  () => import('@/apps/tenant/views/attendance/AttendanceOccurrencesView'),
);
const TenantAttendanceGrid = lazy(
  () => import('@/apps/tenant/views/attendance/AttendanceGridView'),
);
const TenantLogin = lazy(() => import('@/apps/tenant/views/LoginView'));
const TenantForgotPassword = lazy(() => import('@/apps/tenant/views/ForgotPasswordView'));
const TenantResetPassword = lazy(() => import('@/apps/tenant/views/ResetPasswordView'));
const TenantDashboard = lazy(() => import('@/apps/tenant/views/DashboardView'));
const TenantAdminDashboard = lazy(() => import('@/apps/tenant/views/AdminDashboardView'));
const TenantUsersIndex = lazy(() => import('@/apps/tenant/views/UsersIndexView'));
const TenantUserProfile = lazy(() => import('@/apps/tenant/views/UserProfileView'));
const TenantUserCreate = lazy(() => import('@/apps/tenant/views/UserCreateView'));
const TenantOrgUnitsIndex = lazy(() => import('@/apps/tenant/views/OrgUnitsListView'));
const TenantOrgChart = lazy(() => import('@/apps/tenant/views/OrgChartView'));
const TenantOrgUnitDetail = lazy(() => import('@/apps/tenant/views/OrgUnitDetailView'));
const TenantLocaleOverrides = lazy(() => import('@/apps/tenant/views/LocaleOverridesView'));
const TenantMailOutbox = lazy(() => import('@/apps/tenant/views/MailOutboxView'));
const TenantSendMessage = lazy(() => import('@/apps/tenant/views/SendMessageView'));
const TenantMailTemplates = lazy(() => import('@/apps/tenant/views/mailTemplates/MailTemplatesView'));
const TenantMailTemplateEditor = lazy(() => import('@/apps/tenant/views/mailTemplates/MailTemplateEditorView'));
const TenantWorkflows = lazy(() => import('@/apps/tenant/views/WorkflowsIndexView'));
const TenantApprovalFlows = lazy(() => import('@/apps/tenant/views/ApprovalFlowsView'));
const TenantAuditLog = lazy(() => import('@/apps/tenant/views/AuditLogView'));
const TenantMediaLibrary = lazy(() => import('@/apps/tenant/views/MediaLibraryView'));
const TenantApprovalFlowEditor = lazy(() => import('@/apps/tenant/views/ApprovalFlowEditorView'));
const TenantWorkflowEditor = lazy(() => import('@/apps/tenant/views/WorkflowEditorView'));
const TenantApprovals = lazy(() => import('@/apps/tenant/views/ApprovalsInboxView'));
// Het beheer-overzicht (issue 912): álle openstaande taken, niet alleen die van jou.
// Een eigen route en geen vlag op de inbox — ander oppervlak, ander recht.
const TenantApprovalsOverview = lazy(() => import('@/apps/tenant/views/ApprovalsOverviewView'));
// Rollen & rechten (#277, Inc 5) — rol-lijst + role-builder. Poort: `role.view`; de
// bewerk-acties hangen aan `role.create`/`role.edit`, wat de server per rol meegeeft.
const TenantRoles = lazy(() => import('@/apps/tenant/views/RolesIndexView'));
const TenantRoleBuilder = lazy(() => import('@/apps/tenant/views/RoleBuilderView'));
const TenantRoleAssignment = lazy(() => import('@/apps/tenant/views/RoleAssignmentView'));
// Annuleer-redenenlijst beheren (#227) — beheer-config.
// Poort: `cancellation_reason.manage`.
const TenantCancellationReasons = lazy(() => import('@/apps/tenant/views/CancellationReasonsView'));
// Thema's beheren (B41, issue 1172) — beheer-config. Poort: `theme.manage`.
const TenantThemes = lazy(() => import('@/apps/tenant/views/ThemesView'));
// Kwalificaties (kwalificaties.md §14.1): de definities op `qualification.view` en wie wat houdt op
// `held_qualification.view`, onder één nav-item. `/qualifications` kiest de eerste pagina waar de
// gebruiker bij mag.
const TenantQualifications = lazy(() => import('@/apps/tenant/views/qualifications/QualificationsView'));
const TenantHeldQualifications = lazy(() => import('@/apps/tenant/views/qualifications/HeldQualificationsView'));
// Instellingen (FB-6, feedback 21): een startpagina met tegels op `/settings`, en elk onderdeel dat vroeger een
// tabblad was op een eigen adres onder `/settings/…`. De startpagina poort op élk recht dat een tegel laat zien
// (`SETTINGS_ATOMS`); de onderdelen op `tenant_config.view`.
const TenantSettingsHub = lazy(() => import('@/apps/tenant/views/SettingsHubView'));
const TenantFailedCertificates = lazy(() => import('@/apps/tenant/views/FailedCertificatesView'));
const TenantDocumentTemplates = lazy(() => import('@/apps/tenant/views/documents/DocumentTemplateListView'));
const TenantDocumentTemplate = lazy(() => import('@/apps/tenant/views/documents/DocumentTemplateView'));
// De document-editor (#578): TipTap in een eigen chunk, alleen op deze route (ADR-0041).
const TenantDocumentEditor = lazy(() => import('@/apps/tenant/views/documentEditor/DocumentEditorView'));
const TenantLogoUpdate = lazy(() => import('@/apps/tenant/views/documents/TenantLogoUpdateView'));
const BcsUpdate = lazy(() => import('@/apps/tenant/views/documents/BcsUpdateView'));
const TenantSettingsModule = lazy(() => import('@/apps/tenant/views/settings/SettingsModuleView'));
const TenantCatalogEditor = lazy(() => import('@/apps/tenant/views/catalogs/CatalogEditorView'));
// Locatie-lijst beheren (#232 inc-1) — beheer-config.
// Poort: `location.manage`. Zelfde beheer-cluster als de annuleer-redenen.
const TenantLocations = lazy(() => import('@/apps/tenant/views/LocationsView'));
const TenantLtiProviders = lazy(() => import('@/apps/tenant/views/LtiProvidersView'));
// Externe-personenlijst beheren (#232 inc-2) — beheer-config.
// Poort: `external_person.manage`. Role-agnostisch: voedt de trainer-picker
// (en later beoordelaar/auditor). Zelfde beheer-cluster als de locaties.
const TenantExternalPeople = lazy(() => import('@/apps/tenant/views/ExternalPeopleView'));
// Externe-bedrijvenlijst beheren (#232, external-parties batch) — beheer-
// config. Poort: `external_company.manage`. Role-agnostisch: een bedrijf
// met `is_educator` voedt de getrapte opleider→docent trainer-picker. Zelfde
// beheer-cluster als de externe personen.
const TenantExternalCompanies = lazy(() => import('@/apps/tenant/views/ExternalCompaniesView'));
// De inrichting van de aanvraag externe opleiding (blok 25): de soorten, hun versies en de editor.
const TenantExternalRequestTypes = lazy(() => import('@/apps/tenant/views/externalRequestTypes/ExternalRequestTypesView'));
const TenantExternalRequestType = lazy(() => import('@/apps/tenant/views/externalRequestTypes/ExternalRequestTypeView'));
const TenantExternalRequestVersionEditor = lazy(() => import('@/apps/tenant/views/externalRequestTypes/ExternalRequestVersionEditorView'));
// Catalogusbeheer — Fase 1 wireframe (#112). Vervangt de bevroren Aurora-views
// (CatalogView/CatalogCreateView/CatalogPathBuilderView, tag
// archive/112-catalog-leerlijn-builder).
const TenantCatalog = lazy(() => import('@/apps/tenant/views/catalog/CatalogAdminListView'));
const TenantCatalogWizard = lazy(() => import('@/apps/tenant/views/catalog/CatalogWizardView'));
// Lerende bladerkant (#179, D11) — kaart-grid + filters op de eindgebruiker-host.
const TenantCatalogBrowse = lazy(() => import('@/apps/tenant/views/catalog/CatalogBrowseView'));
// Lerende activiteit-detail (#177) — inschrijven op uitvoeringen. Learner-kant,
// alleen RequireAuth (via de parent-outlet), bereikbaar op URL /catalog/:activityId.
const TenantCatalogDetail = lazy(() => import('@/apps/tenant/views/catalog/CatalogDetailView'));
// De speler (ADR-0033 § 4). Lazy en dat is hier extra van belang: de
// SCORM-runtime is een zware bundel en de meeste bezoekers van een catalogus
// starten geen cursus.
const TenantElearningPlayer = lazy(() => import('@/apps/tenant/views/ElearningPlayerView'));
// Dezelfde cursus, zonder inloggen (ADR-0033 § 5). Een eigen view en geen
// vlag op de bovenstaande: daar zit voortgang, een bookmark en een resultaat in,
// en hier bestaat geen van die drie.
const TenantAnonymousElearning = lazy(() => import('@/apps/tenant/views/AnonymousElearningView'));
// Een vragenlijst invullen via de link uit de mail (ADR-0036, besluit 3). Zonder inloggen.
const TenantQuestionnaireInvite = lazy(
  () => import('@/apps/tenant/views/questionnaireInvite/QuestionnaireInviteView'),
);
// Toegangsgroepen (issue 134) — herbruikbare doelgroepen. Beheer-oppervlak;
// poort `access_group.view` voor de lijst, `create`/`edit` voor de bouwer zelf
// (die gate't de server, dit scherm verbergt alleen de knoppen).
const TenantAccessGroups = lazy(() => import('@/apps/tenant/views/access/AccessGroupsView'));
const TenantAccessGroupEditor = lazy(() => import('@/apps/tenant/views/access/AccessGroupEditorView'));
// De vragenlijsten (vragenlijsten.md §2.2): vragen, categorieën en formulieren. Lezen op
// `questionnaire.view`; aanmaken op `questionnaire.manage`, bewerken toont zonder dat recht alleen-lezen.
const TenantQuestions = lazy(() => import('@/apps/tenant/views/questionnaires/QuestionsListView'));
const TenantQuestionEditor = lazy(() => import('@/apps/tenant/views/questionnaires/QuestionEditorView'));
const TenantQuestionCategories = lazy(() => import('@/apps/tenant/views/questionnaires/CategoriesListView'));
const TenantQuestionCategoryEditor = lazy(() => import('@/apps/tenant/views/questionnaires/CategoryEditorView'));
const TenantQuestionnaireForms = lazy(() => import('@/apps/tenant/views/questionnaires/FormsListView'));
const TenantQuestionnaireFormEditor = lazy(() => import('@/apps/tenant/views/questionnaires/FormEditorView'));
const TenantAssignmentStatus = lazy(() => import('@/apps/tenant/views/questionnaires/AssignmentStatusView'));
const TenantAssignmentsOverview = lazy(() => import('@/apps/tenant/views/questionnaires/AssignmentsOverviewView'));
// Losse vragenlijsten (vragenlijsten.md §5, D9): de lijst op `questionnaire.view`, maken en bewerken
// vóór het versturen op het eigen, gevaarlijke recht `survey.send`.
const TenantSurveys = lazy(() => import('@/apps/tenant/views/questionnaires/SurveysListView'));
const TenantSurveyEditor = lazy(() => import('@/apps/tenant/views/questionnaires/SurveyEditorView'));
// Wie een nog niet verstuurde losse vragenlijst nu zou krijgen, als boom. Lezen op `questionnaire.view`;
// namen vragen daar `user.view` bij, en dat bewaakt de server.
const TenantSurveyRecipients = lazy(() => import('@/apps/tenant/views/questionnaires/SurveyRecipientsView'));
// Admin-wachtlijst van een uitvoering (#204) — handmatige herordening.
// Poort: `enrollment.view`.
const TenantOccurrenceWaitlist = lazy(() => import('@/apps/tenant/views/catalog/OccurrenceWaitlistView'));
// Planning-overzicht per activiteit (#228 inc-1) — uitvoeringen-lijst + status-
// beheer + simpele stats. Poort: `occurrence.view`.
const TenantPlanning = lazy(() => import('@/apps/tenant/views/catalog/PlanningView'));
// Uitvoering-detail per uitvoering (#228 inc-3) — deelnemers + meta + ingebedde
// wachtlijst. Poort: `occurrence.view`.
const TenantOccurrenceDetail = lazy(() => import('@/apps/tenant/views/catalog/OccurrenceDetailView'));
// Een uitvoering plannen op een eigen pagina (D28); poort: `occurrence.edit`.
const TenantOccurrenceEdit = lazy(() => import('@/apps/tenant/views/catalog/OccurrenceEditPage'));
const TenantOccurrenceBulkEdit = lazy(() => import('@/apps/tenant/views/catalog/OccurrenceBulkEditPage'));
// Resultaten van een e-learning (ADR-0033 § 6) — het scherm waar de
// score-correctie op hangt.
const TenantElearningResults = lazy(() => import('@/apps/tenant/views/catalog/ElearningResultsView'));
// De vragenlijsten bij een training (vragenlijsten.md § 5). Poort: `questionnaire.assign`.
const TenantActivityAssignments = lazy(() => import('@/apps/tenant/views/questionnaires/ActivityAssignmentsView'));
const TenantAssignmentEditor = lazy(() => import('@/apps/tenant/views/questionnaires/AssignmentEditorView'));
// Mijn inschrijvingen (#181) — de eigen inschrijvingen van de lerende, read-only.
// Elke ingelogde gebruiker (RequireAuth via de parent-outlet).
const TenantMyEnrollments = lazy(() => import('@/apps/tenant/views/MyEnrollmentsView'));
// De persoonlijke pagina van een leerlijn (issue 815) — op je eigen inschrijving, niet op de
// catalogus. Elke ingelogde gebruiker; de server geeft een 404 als het anker niet van jou is.
const TenantMyLearningPath = lazy(() => import('@/apps/tenant/views/myLearningPath/MyLearningPathView'));
const TenantMyQualifications = lazy(() => import('@/apps/tenant/views/myQualifications/MyQualificationsView'));
const TenantMyExternalRequests = lazy(() => import('@/apps/tenant/views/myExternalRequests/MyExternalRequestsView'));
const TenantExternalRequest = lazy(() => import('@/apps/tenant/views/myExternalRequests/ExternalRequestView'));
const TenantMyQuestionnaires = lazy(() => import('@/apps/tenant/views/myQuestionnaires/MyQuestionnairesView'));
const TenantMyQuestionnaireFill = lazy(() => import('@/apps/tenant/views/myQuestionnaires/MyQuestionnaireFillView'));
const TenantMyTestResult = lazy(() => import('@/apps/tenant/views/myQuestionnaires/MyTestResultView'));
const TenantGradingQueue = lazy(() => import('@/apps/tenant/views/grading/GradingQueueView'));
const TenantGradeTest = lazy(() => import('@/apps/tenant/views/grading/GradeTestView'));
const TenantResultsOccurrences = lazy(() => import('@/apps/tenant/views/results/ResultsOccurrencesView'));
const TenantResultsOccurrence = lazy(() => import('@/apps/tenant/views/results/ResultsOccurrenceView'));
const TenantResultsRule = lazy(() => import('@/apps/tenant/views/results/ResultsRuleView'));
const WorkflowHelp = lazy(() => import('@shared/views/WorkflowHelpView'));
const ProfileView = lazy(() => import('@shared/views/ProfileView'));


function Loading() {
  return <div aria-busy="true" style={{ padding: '2rem' }}>Laden…</div>;
}

function Lazy({ children }: { children: ReactNode }) {
  return <Suspense fallback={<Loading />}>{children}</Suspense>;
}

/**
 * Zet het toetsoppervlak voor het scherm eronder: dezelfde schermen als de vragenlijsten, met de
 * routes, rechten en teksten van de toetsen (zie `questionnaireSurface`). Binnen de guard, zodat
 * een scherm nooit zonder zijn recht begint te laden.
 */
function TestSurface({ children }: { children: ReactNode }) {
  return <SurfaceProvider surface="test">{children}</SurfaceProvider>;
}

/**
 * Surface-aware `/catalog` (D11, #179): op de beheer-host (`surface==='manage'`)
 * én voor wie `activity.view` houdt rendert de bestaande beheer-lijst; in alle andere
 * gevallen (o.a. de eindgebruiker-host) de lerende bladerkant. Auth is al
 * afgedwongen door de omliggende `<RequireAuth>`-outlet. We wachten op de
 * meta-hydratie zodat er geen flits van de verkeerde view is.
 */
/**
 * Oude activiteit-adressen naar hun nieuwe plek (issue 1266). Alles onder
 * `/catalog/<id>` is naar `/activity/<id>` verhuisd omdat die twee segmenten nodig zijn
 * voor een catalogus-pagina (`/catalog/:slug`).
 *
 * ⚠️ DIT IS GEEN NETHEID MAAR EEN VERSPREID ADRES. `/catalog/:id/open` is de link die een
 * beheerder kopieert en in een intranet, een mail of een ander LMS plakt. "Bewaarde links
 * mogen breken" ging over bookmarks; dit is iets anders, en het breekt bij iemand die wij
 * niet kunnen bereiken.
 *
 * De redirect is alleen eenduidig zolang een catalogus-slug nooit uuid-vormig is. Dat is
 * geen afspraak maar een validatieregel op de server (`CatalogRequest`).
 */
function LegacyActivityRedirect() {
  const { pathname, search, hash } = useLocation();
  return <Navigate to={`${pathname.replace(/^\/catalog\//, '/activity/')}${search}${hash}`} replace />;
}

/** Een uuid als eerste segment hoort bij een activiteit; een slug bij een catalogus. */
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * `/catalog/<segment>` betekent sinds issue 135 twee dingen, en dit is waar de knoop wordt
 * doorgehakt: een **uuid** is een oud activiteit-adres en gaat door naar `/activity/<id>`, al
 * het andere is de slug van een catalogus-pagina.
 *
 * ⚠️ DIT WERKT ALLEEN OMDAT EEN SLUG NOOIT UUID-VORMIG MAG ZIJN. Dat is geen afspraak maar een
 * validatieregel én een CHECK in de database — een gedrag dat van een afspraak afhangt, is geen
 * gedrag.
 */
function CatalogSlugRoute() {
  const { slug } = useParams<{ slug: string }>();
  return slug !== undefined && UUID.test(slug)
    ? <LegacyActivityRedirect />
    : <Lazy><TenantCatalogBrowse /></Lazy>;
}

/**
 * Kaal `/catalog` stuurt door naar de eerste catalogus die déze gebruiker mag openen, op
 * volgorde (besluit 19-10). Geen overzichtspagina: de zijbalk ís de opsomming, en een pagina
 * die herhaalt wat daar al staat is een klik zonder informatie — bij één catalogus, het normale
 * geval, ronduit hinderlijk.
 *
 * Is er niets beschikbaar, dan blijft de bladerkant staan met zijn eigen lege toestand. Die
 * moest er toch komen, en hij zegt meer dan een doorverwijzing naar nergens.
 */
function CatalogIndexRoute() {
  const menu = useCatalogMenu();

  if (menu.isLoading) {
    return <Loading />;
  }

  const first = (menu.data ?? []).find((entry) => entry.in_main_nav) ?? menu.data?.[0];

  return first ? <Navigate to={`/catalog/${first.slug}`} replace /> : <Lazy><TenantCatalogBrowse /></Lazy>;
}

function CatalogRoute() {
  const user = useAuthStore((s) => s.user);
  const metaQuery = useTenantMeta();

  if (metaQuery.isLoading) {
    return <Loading />;
  }
  // Beheer-oppervlak hangt aan het recht, niet aan de rolnaam (#277, RG22) — plus het
  // atoom van dit scherm zelf (UI8), zodat de nav en deze route hetzelfde antwoord geven.
  // Zonder `activity.view` verbergt de nav de lijst en hoort een deep-link hier dus ook
  // niet de beheer-lijst te tonen.
  //
  // Sinds issue 1266 rendert dit adres de beheer-lijst niet meer zelf maar stuurt het door
  // naar haar eigen plek. Eén scherm, één adres: anders betekent `/catalog` twee dingen
  // afhankelijk van wie er kijkt, en dat is precies waarom de terug-links en de nav hier
  // uit elkaar liepen.
  const held = user?.permissions ?? [];
  const mayManageCatalog = held.includes('admin_console.enter') && held.includes('activity.view');
  if (metaQuery.data?.surface === 'manage' && mayManageCatalog) {
    return <Navigate to="/activity" replace />;
  }
  return <CatalogIndexRoute />;
}

/**
 * Surface-aware `/` (issue 1036): op de beheer-host, voor wie de console binnen mag, het
 * beheerders-dashboard (`beheerders-dashboard.md`); in alle andere gevallen *Mijn dashboard*. Zelfde vorm
 * als `CatalogRoute`: wachten op de meta-hydratie, zodat er geen flits van het verkeerde dashboard is. De
 * server houdt dezelfde grens aan — de blokken van het beheerders-dashboard staan achter
 * `admin_console.enter`, en dat atoom telt alleen op de beheer-host.
 */
function DashboardRoute() {
  const held = useAuthStore((s) => s.user?.permissions) ?? [];
  const metaQuery = useTenantMeta();

  if (metaQuery.isLoading) {
    return <Loading />;
  }
  if (metaQuery.data?.surface === 'manage' && held.includes('admin_console.enter')) {
    return <Lazy><TenantAdminDashboard /></Lazy>;
  }
  return <Lazy><TenantDashboard /></Lazy>;
}

/**
 * `/qualifications`: de definities voor wie ze mag zien, anders — met alleen `held_qualification.view` —
 * door naar *wie wat houdt*. De guard eromheen laat beide atomen binnen; deze keuze staat dus niet
 * in het menu maar in de route, zodat het ene item voor beide gebruikers een werkende bestemming heeft.
 */
function QualificationsEntry() {
  const mayViewDefinitions = useAuthStore((s) => (s.user?.permissions ?? []).includes('qualification.view'));

  return mayViewDefinitions
    ? <Lazy><TenantQualifications /></Lazy>
    : <Navigate to="/qualifications/held" replace />;
}

export function AppRoutes() {
  const mode = useAppMode();

  if (mode === 'superadmin') {
    return (
      <Routes>
        <Route
          path="/login"
          element={
            <GuestOnly>
              <Lazy><SuperadminLogin /></Lazy>
            </GuestOnly>
          }
        />
        <Route element={<RequireAuth><AppShell /></RequireAuth>}>
          <Route index element={<Lazy><SuperadminDashboard /></Lazy>} />
          <Route path="tenants" element={<Lazy><SuperadminTenantsIndex /></Lazy>} />
          <Route path="tenants/:tenantId" element={<Lazy><SuperadminTenantSetup /></Lazy>} />
          <Route path="tenants/:tenantId/platform-settings" element={<Lazy><SuperadminTenantPlatformSettings /></Lazy>} />
          <Route path="tenants/:tenantId/org-structure" element={<Lazy><SuperadminOrgStructure /></Lazy>} />
          <Route path="tenants/:tenantId/audit-log" element={<Lazy><SuperadminAuditLog /></Lazy>} />
          <Route path="tenants/:tenantId/imports" element={<Lazy><SuperadminImportsOverview /></Lazy>} />
          <Route path="tenants/:tenantId/imports/runs/:runId" element={<Lazy><SuperadminImportRunDetail /></Lazy>} />
          <Route path="tenants/:tenantId/imports/sources/new" element={<Lazy><SuperadminImportSourceWizard /></Lazy>} />
          <Route path="tenants/:tenantId/imports/sources/:sourceId" element={<Lazy><SuperadminImportSourceWizard /></Lazy>} />
          <Route path="tenants/:tenantId/imports/sources/:sourceId/run" element={<Lazy><SuperadminImportSourceRun /></Lazy>} />
          <Route path="platform-assets" element={<Lazy><SuperadminPlatformAssets /></Lazy>} />
          <Route path="platform-locales" element={<Lazy><SuperadminPlatformLocales /></Lazy>} />
          <Route path="settings" element={<Lazy><SuperadminPlatformSettings /></Lazy>} />
          <Route path="mail-outbox" element={<Lazy><SuperadminMailOutbox /></Lazy>} />
          <Route path="superadmins" element={<Lazy><Superadmins /></Lazy>} />
          <Route path="platform-workflows" element={<Lazy><SuperadminPlatformWorkflows /></Lazy>} />
          <Route path="platform-workflows/help" element={<Lazy><WorkflowHelp /></Lazy>} />
          <Route path="platform-workflows/:id" element={<Lazy><SuperadminPlatformWorkflowEditor /></Lazy>} />
          <Route path="mail-templates" element={<Lazy><SuperadminMailTemplates /></Lazy>} />
          <Route path="mail-templates/:slug" element={<Lazy><SuperadminMailTemplateEditor /></Lazy>} />
          <Route path="profile" element={<Lazy><ProfileView /></Lazy>} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Route>
      </Routes>
    );
  }

  return (
    <Routes>
      <Route
        path="/login"
        element={
          <GuestOnly>
            <Lazy><TenantLogin /></Lazy>
          </GuestOnly>
        }
      />
      {/* Public password lifecycle for auth_driver='password' tenants (#105).
          Reset doubles as the activation screen for invited users. */}
      <Route
        path="/forgot-password"
        element={
          <GuestOnly>
            <Lazy><TenantForgotPassword /></Lazy>
          </GuestOnly>
        }
      />
      <Route
        path="/reset-password"
        element={
          <GuestOnly>
            <Lazy><TenantResetPassword /></Lazy>
          </GuestOnly>
        }
      />
      {/* Een cursus die zonder inloggen open staat (ADR-0033 § 5, issue 507).
          ⚠️ BEWUST GEEN `GuestOnly`: die stuurt een ingelogde gebruiker weg, en
          juist de beheerder die de link net heeft aangezet wil hem kunnen
          controleren. En bewust buiten de `AppShell`: wie hier komt heeft geen
          account, dus een menu naar schermen waar hij niet in mag hoort er niet.
          De poort zit op de server (`allow_anonymous`, standaard uit) — een
          activiteit die het niet toestaat geeft hier een 404 en dus een melding
          op het scherm. */}
      <Route
        path="/activity/:activityId/open"
        element={<Lazy><TenantAnonymousElearning /></Lazy>}
      />
      {/* Het oude adres van diezelfde open cursus (issue 1266). Dit is de link die het meest
          buiten het systeem rondgaat, dus hij blijft werken — en net als het adres hierboven
          **buiten** de inlog-outlet, anders zou een bezoeker zonder account via de redirect
          alsnog op het inlogscherm belanden. */}
      <Route
        path="/catalog/:activityId/open"
        element={<LegacyActivityRedirect />}
      />
      {/* Een vragenlijst invullen via de link uit de mail (ADR-0036, besluit 3). Zelfde
          vorm als de open cursus hierboven: geen `GuestOnly` (een ingelogde collega vult
          hem ook gewoon in) en buiten de `AppShell`. Het token staat in het fragment
          (`#t=…`) en gaat in de body mee, nooit in de URL. */}
      <Route
        path="/questionnaires/invite"
        element={<Lazy><TenantQuestionnaireInvite /></Lazy>}
      />
      <Route element={<RequireAuth><AppShell /></RequireAuth>}>
        <Route index element={<DashboardRoute />} />
        <Route
          path="users"
          element={<RequireAdminConsole permission="user.view"><Lazy><TenantUsersIndex /></Lazy></RequireAdminConsole>}
        />
        {/* `users/new` staat vóór `users/:id` zodat "new" niet als id wordt gelezen —
            zelfde reden als bij `roles/new`. */}
        <Route
          path="users/new"
          element={<RequireAdminConsole permission="user.create"><Lazy><TenantUserCreate /></Lazy></RequireAdminConsole>}
        />
        <Route
          path="users/:id"
          element={<RequireAdminConsole permission="user.view"><Lazy><TenantUserProfile /></Lazy></RequireAdminConsole>}
        />
        {/* Rol toekennen met bereik. Drie segmenten eindigend op een statisch `roles`, dus
            geen botsing met users/:id. Gate't op het toekennings-atoom en niet op
            `user.view`: meekijken met een profiel is iets anders dan rechten uitdelen. */}
        <Route
          path="users/:id/roles"
          element={
            <RequireAdminConsole permission="role_assignment.grant">
              <Lazy><TenantRoleAssignment /></Lazy>
            </RequireAdminConsole>
          }
        />
        <Route
          path="org-units"
          element={<RequireAdminConsole permission="org_unit.view"><Lazy><TenantOrgUnitsIndex /></Lazy></RequireAdminConsole>}
        />
        {/* Rollen & rechten (#277). `/roles/new` staat vóór `/roles/:key` zodat "new"
            niet als sleutel wordt gelezen. */}
        <Route
          path="roles"
          element={<RequireAdminConsole permission="role.view"><Lazy><TenantRoles /></Lazy></RequireAdminConsole>}
        />
        <Route
          path="roles/new"
          element={<RequireAdminConsole permission="role.create"><Lazy><TenantRoleBuilder /></Lazy></RequireAdminConsole>}
        />
        <Route
          path="roles/:key"
          element={<RequireAdminConsole permission="role.view"><Lazy><TenantRoleBuilder /></Lazy></RequireAdminConsole>}
        />
        {/* Toegangsgroepen (issue 134). `new` staat vóór `:id` zodat "new" niet
            als id wordt gelezen — zelfde reden als bij rollen hierboven.
            De bouwer gate't op `view` en niet op `edit`: wie alleen mag kijken
            hoort een groep te kunnen openen; de opslaan-knop is dan de poort, en
            de echte grens ligt op de server. */}
        <Route
          path="access-groups"
          element={<RequireAdminConsole permission="access_group.view"><Lazy><TenantAccessGroups /></Lazy></RequireAdminConsole>}
        />
        <Route
          path="access-groups/new"
          element={<RequireAdminConsole permission="access_group.create"><Lazy><TenantAccessGroupEditor /></Lazy></RequireAdminConsole>}
        />
        <Route
          path="access-groups/:id"
          element={<RequireAdminConsole permission="access_group.view"><Lazy><TenantAccessGroupEditor /></Lazy></RequireAdminConsole>}
        />
        <Route
          path="org-units/chart"
          element={<RequireAdminConsole permission="org_unit.view"><Lazy><TenantOrgChart /></Lazy></RequireAdminConsole>}
        />
        <Route
          path="org-units/:id"
          element={<RequireAdminConsole permission="org_unit.view"><Lazy><TenantOrgUnitDetail /></Lazy></RequireAdminConsole>}
        />
        <Route path="catalog" element={<CatalogRoute />} />
        {/* Oude activiteit-adressen blijven werken. `/catalog/:id` en alles eronder stond tot
            issue 1266 op de catalogus; die twee segmenten zijn nu nodig voor een
            catalogus-pagina (`/catalog/:slug`). Een beheerder deelt de open-link van een cursus
            buiten het systeem — in een intranet, een mail, een ander LMS — en die mag niet
            doodlopen. De redirect kan alleen bestaan omdat een slug nooit uuid-vormig mag zijn;
            dat wordt server-side afgedwongen, niet afgesproken. */}
        <Route path="catalog/:activityId/*" element={<LegacyActivityRedirect />} />
        <Route path="catalog/:slug" element={<CatalogSlugRoute />} />
        {/* De activiteiten-masterlijst van het beheer. Stond tot issue 1266 op `/catalog`, samen
            met de lerende bladerkant op datzelfde adres; nu heeft elk zijn eigen plek. */}
        <Route
          path="activity"
          element={<RequireAdminConsole permission="activity.view"><Lazy><TenantCatalog /></Lazy></RequireAdminConsole>}
        />
        <Route
          path="activity/new"
          element={<RequireAdminConsole permission="activity.create"><Lazy><TenantCatalogWizard /></Lazy></RequireAdminConsole>}
        />
        <Route
          path="activity/:id/edit"
          element={<RequireAdminConsole permission="activity.edit"><Lazy><TenantCatalogWizard /></Lazy></RequireAdminConsole>}
        />
        {/* Admin-wachtlijst van een uitvoering (#204) — handmatige herordening.
            Vier segmenten, dus geen botsing met catalog/:activityId of :id/edit. */}
        <Route
          path="activity/:activityId/occurrences/:occurrenceId/waitlist"
          element={<RequireAdminConsole permission="enrollment.view"><Lazy><TenantOccurrenceWaitlist /></Lazy></RequireAdminConsole>}
        />
        {/* Planning-overzicht per activiteit (#228 inc-1). Twee segmenten eindigend
            op een statisch `planning`, dus geen botsing met catalog/:activityId of
            :id/edit. */}
        <Route
          path="activity/:activityId/planning"
          element={<RequireAdminConsole permission="occurrence.view"><Lazy><TenantPlanning /></Lazy></RequireAdminConsole>}
        />
        {/* Een uitvoering plannen (D28): een nieuwe, of de bestaande bewerken. `new` is statisch en wint van
            `:occurrenceId` hieronder. */}
        {/* Alle uitvoeringen in één tabel bewerken (issue 265). Statisch `bulk`, dus het wint van
            `:occurrenceId` hieronder, net als `new`. */}
        <Route
          path="activity/:activityId/planning/bulk"
          element={<RequireAdminConsole permission="occurrence.edit"><Lazy><TenantOccurrenceBulkEdit /></Lazy></RequireAdminConsole>}
        />
        <Route
          path="activity/:activityId/planning/new"
          element={<RequireAdminConsole permission="occurrence.edit"><Lazy><TenantOccurrenceEdit /></Lazy></RequireAdminConsole>}
        />
        <Route
          path="activity/:activityId/planning/:occurrenceId/edit"
          element={<RequireAdminConsole permission="occurrence.edit"><Lazy><TenantOccurrenceEdit /></Lazy></RequireAdminConsole>}
        />
        {/* Uitvoering-detail (#228 inc-3): deelnemers + meta + ingebedde wachtlijst.
            Genest onder planning; drie segmenten met een statisch `planning`, dus
            geen botsing met catalog/:activityId of :id/edit. */}
        <Route
          path="activity/:activityId/planning/:occurrenceId"
          element={<RequireAdminConsole permission="occurrence.view"><Lazy><TenantOccurrenceDetail /></Lazy></RequireAdminConsole>}
        />
        {/* De resultaten van een e-learning (ADR-0033 § 6): wie is begonnen, wat
            staat er, en het corrigeren daarvan. `enrollment.view` als poort — dit
            is deelname-data en geen catalogus-informatie; corrigeren zit
            daarbinnen nog achter een eigen atoom (server-side). Twee segmenten
            eindigend op een statisch `results`, dus geen botsing met
            catalog/:activityId of :id/edit. */}
        <Route
          path="activity/:activityId/results"
          element={<RequireAdminConsole permission="enrollment.view"><Lazy><TenantElearningResults /></Lazy></RequireAdminConsole>}
        />
        {/* De vragenlijsten bij een training (vragenlijsten.md § 5): toewijzen, pauzeren,
            hervatten, archiveren, verwijderen. Alles hier is `questionnaire.assign`; de pagina
            heeft ook een sectie voor de toetsen (`test.assign`), dus elk van beide laat binnen
            en de secties tonen alleen wat het recht toestaat. Twee segmenten eindigend op een statisch `questionnaires`, dus geen
            botsing met catalog/:activityId of :id/edit. */}
        <Route
          path="activity/:activityId/questionnaires"
          element={<RequireAdminConsole permission={['questionnaire.assign', 'test.assign']}><Lazy><TenantActivityAssignments /></Lazy></RequireAdminConsole>}
        />
        {/* De editor van een toewijzing op een eigen pagina. Zelfde recht als de lijst; `new`
            staat vóór `:assignmentId/edit`, zodat een nieuw adres nooit als id gelezen wordt. */}
        <Route
          path="activity/:activityId/questionnaires/new"
          element={<RequireAdminConsole permission="questionnaire.assign"><Lazy><TenantAssignmentEditor /></Lazy></RequireAdminConsole>}
        />
        <Route
          path="activity/:activityId/questionnaires/:assignmentId/edit"
          element={<RequireAdminConsole permission="questionnaire.assign"><Lazy><TenantAssignmentEditor /></Lazy></RequireAdminConsole>}
        />
        {/* De editor van een toetsregel: dezelfde pagina, op het toetsoppervlak. `new` vóór `:assignmentId`. */}
        <Route
          path="activity/:activityId/tests/new"
          element={<RequireAdminConsole permission="test.assign"><TestSurface><Lazy><TenantAssignmentEditor /></Lazy></TestSurface></RequireAdminConsole>}
        />
        <Route
          path="activity/:activityId/tests/:assignmentId/edit"
          element={<RequireAdminConsole permission="test.assign"><TestSurface><Lazy><TenantAssignmentEditor /></Lazy></TestSurface></RequireAdminConsole>}
        />
        {/* Lerende activiteit-detail — inschrijven op uitvoeringen (#177). Voor
            elke ingelogde gebruiker (RequireAuth via de parent-outlet); een
            enkel dynamisch segment botst niet met catalog/new of :id/edit. */}
        <Route path="activity/:activityId" element={<Lazy><TenantCatalogDetail /></Lazy>} />
        {/* De speler (ADR-0033 § 4). Onder de activiteit en met een statisch
            segment erachter, dus geen botsing met `catalog/:activityId`. Alleen
            RequireAuth via de parent-outlet: meedoen aan een cursus waarop je
            bent ingeschreven is geen beheer-handeling, en de poort is de
            inschrijving — die wordt server-side gecontroleerd. */}
        <Route path="activity/:activityId/play" element={<Lazy><TenantElearningPlayer /></Lazy>} />
        {/* Mijn inschrijvingen — read-only status-overzicht voor elke ingelogde
            gebruiker (#181). Statisch segment, dus geen botsing met catalog/:id. */}
        <Route path="my-enrollments" element={<Lazy><TenantMyEnrollments /></Lazy>} />
        {/* De persoonlijke pagina van een leerlijn (issue 815), en de speler van een onderdeel
            daaronder: wie een onderdeel vanaf de leerlijn opent blijft binnen zijn inschrijving en
            gaat niet langs de catalogus-pagina van dat onderdeel (die kan een 404 geven). */}
        <Route path="my-enrollments/learning-paths/:enrollmentId" element={<Lazy><TenantMyLearningPath /></Lazy>} />
        <Route
          path="my-enrollments/learning-paths/:enrollmentId/parts/:activityId/play"
          element={<Lazy><TenantElearningPlayer /></Lazy>}
        />
        {/* Mijn kwalificaties (kwalificaties.md § 14.3): wat ik houd en gehouden heb, per kwalificatie met de
            grond erbij. Geen atoom: eigen data, de server geeft alleen mijn rijen. */}
        <Route path="my-qualifications" element={<Lazy><TenantMyQualifications /></Lazy>} />
        {/* Mijn aanvragen externe opleiding (blok 25, plak 2). Geen atoom op de lijst: eigen data, de server
            geeft alleen mijn aanvragen. `new` staat vóór `:id`, zodat "new" nooit als id gelezen wordt. Het
            recht om aan te vragen bewaakt de server; zonder recht of zonder live soort toont de pagina geen
            formulier. */}
        <Route path="my-requests" element={<Lazy><TenantMyExternalRequests /></Lazy>} />
        <Route path="my-requests/new" element={<Lazy><TenantExternalRequest /></Lazy>} />
        <Route path="my-requests/:id" element={<Lazy><TenantExternalRequest /></Lazy>} />
        {/* Mijn vragenlijsten (vragenlijsten.md § 6.1): wat er voor mij openstaat, en het
            invullen ervan in de app. Dezelfde uitnodiging als die in de mail; de server
            bepaalt wat van mij is en nog openstaat. Geen atoom: leerling-kant. */}
        <Route path="my-questionnaires" element={<Lazy><TenantMyQuestionnaires /></Lazy>} />
        <Route path="my-questionnaires/:responseId" element={<Lazy><TenantMyQuestionnaireFill /></Lazy>} />
        {/* De uitslag van een nagekeken toets. Geen atoom, net als de lijst: de server geeft alleen
            een uitslag aan de eigenaar, en alleen als hij zichtbaar is (anders dezelfde 404). */}
        <Route path="my-questionnaires/:responseId/result" element={<Lazy><TenantMyTestResult /></Lazy>} />

        {/* Presentie (presentie-en-afronden.md § 4). Achter het ATOOM, niet achter
            RequireAdminConsole: de docent legt dit vast en die is geen beheerder —
            het teacher-sjabloon heeft `admin_console.enter` niet, en dat hoort zo
            (RG22). Vandaar RequirePermission. */}
        <Route
          path="attendance"
          element={
            <RequirePermission permission="attendance.manage">
              <Lazy><TenantAttendanceOccurrences /></Lazy>
            </RequirePermission>
          }
        />
        <Route
          path="attendance/:occurrenceId"
          element={
            <RequirePermission permission="attendance.manage">
              <Lazy><TenantAttendanceGrid /></Lazy>
            </RequirePermission>
          }
        />
        {/* Toetsen nakijken (basis-T). Zelfde reden als Presentie: de nakijker is meestal een docent
            zonder beheer-recht, dus het ATOOM `test.grade` is de poort en niet RequireAdminConsole —
            óf een aanwijzing als nakijker, zonder het atoom (besluit PO 16-10). Het hoort bij de
            toetsen, maar staat bewust niet onder het beheermenu Toetsen. */}
        <Route
          path="grading"
          element={
            <RequireGrader>
              <Lazy><TenantGradingQueue /></Lazy>
            </RequireGrader>
          }
        />
        <Route
          path="grading/:responseId"
          element={
            <RequireGrader>
              <Lazy><TenantGradeTest /></Lazy>
            </RequireGrader>
          }
        />
        {/* Resultaten van evaluaties (vragenlijsten-slice 1b, R10/R11). Zelfde reden als Presentie en Nakijken:
            de docent leest de uitkomst van zijn eigen uitvoeringen en heeft geen beheer-recht, dus het ATOOM
            `questionnaire.results` is de poort en niet RequireAdminConsole. Het regelrapport staat achter
            hetzelfde atoom: wie het alleen met een eigen bereik houdt, krijgt van de API een 404 en het scherm
            toont dan *niet gevonden*; de route raadt dat niet uit het atoom. */}
        <Route
          path="results"
          element={
            <RequirePermission permission="questionnaire.results">
              <Lazy><TenantResultsOccurrences /></Lazy>
            </RequirePermission>
          }
        />
        <Route
          path="results/occurrences/:occurrenceId"
          element={
            <RequirePermission permission="questionnaire.results">
              <Lazy><TenantResultsOccurrence /></Lazy>
            </RequirePermission>
          }
        />
        <Route
          path="results/rules/:assignmentId"
          element={
            <RequirePermission permission="questionnaire.results">
              <Lazy><TenantResultsRule /></Lazy>
            </RequirePermission>
          }
        />
        <Route
          path="texts"
          element={<RequireAdminConsole permission="text_label.manage"><Lazy><TenantLocaleOverrides /></Lazy></RequireAdminConsole>}
        />
        <Route
          path="mail-outbox"
          element={<RequireAdminConsole permission="mail_outbox.view"><Lazy><TenantMailOutbox /></Lazy></RequireAdminConsole>}
        />
        <Route
          path="messages"
          element={<RequireAdminConsole permission="broadcast.send"><Lazy><TenantSendMessage /></Lazy></RequireAdminConsole>}
        />
        <Route
          path="mail-templates"
          element={<RequireAdminConsole permission="mail_template.view"><Lazy><TenantMailTemplates /></Lazy></RequireAdminConsole>}
        />
        <Route
          path="mail-templates/:slug"
          element={<RequireAdminConsole permission="mail_template.view"><Lazy><TenantMailTemplateEditor /></Lazy></RequireAdminConsole>}
        />
        <Route
          path="themes"
          element={<RequireAdminConsole permission="theme.manage"><Lazy><TenantThemes /></Lazy></RequireAdminConsole>}
        />
        <Route
          path="cancellation-reasons"
          element={<RequireAdminConsole permission="cancellation_reason.manage"><Lazy><TenantCancellationReasons /></Lazy></RequireAdminConsole>}
        />
        <Route
          path="qualifications"
          element={<RequireAdminConsole permission={['qualification.view', 'held_qualification.view']}><QualificationsEntry /></RequireAdminConsole>}
        />
        <Route
          path="qualifications/held"
          element={<RequireAdminConsole permission="held_qualification.view"><Lazy><TenantHeldQualifications /></Lazy></RequireAdminConsole>}
        />
        {/* Certificaten die niet gemaakt konden worden (#352): de lijst achter de dashboardtaak. */}
        <Route
          path="certificates/failed"
          element={<RequireAdminConsole permission="document_template.edit"><Lazy><TenantFailedCertificates /></Lazy></RequireAdminConsole>}
        />
        {/* De sjabloon-bibliotheek en de pagina van een sjabloon met zijn geschiedenis (#578). */}
        <Route
          path="document-templates"
          element={<RequireAdminConsole permission="document_template.view"><Lazy><TenantDocumentTemplates /></Lazy></RequireAdminConsole>}
        />
        <Route
          path="document-templates/:id/edit"
          element={<RequireAdminConsole permission="document_template.edit"><Lazy><TenantDocumentEditor /></Lazy></RequireAdminConsole>}
        />
        <Route
          path="document-templates/:id"
          element={<RequireAdminConsole permission="document_template.view"><Lazy><TenantDocumentTemplate /></Lazy></RequireAdminConsole>}
        />
        {/* Een nieuw logo van de omgeving op de certificaten: dezelfde soort taak (B1). */}
        <Route
          path="document-templates/updates/tenant-logo"
          element={<RequireAdminConsole permission="document_template.edit"><Lazy><TenantLogoUpdate /></Lazy></RequireAdminConsole>}
        />
        {/* Een nieuwe BCS-versie op een onveranderde kopie: het oude en het nieuwe naast elkaar. */}
        <Route
          path="document-templates/updates/:id"
          element={<RequireAdminConsole permission="document_template.edit"><Lazy><BcsUpdate /></Lazy></RequireAdminConsole>}
        />
        {/* De startpagina van Instellingen: tegels per thema. Open voor wie minstens één tegel ziet. */}
        <Route
          path="settings"
          element={<RequireAdminConsole permission={SETTINGS_ATOMS}><Lazy><TenantSettingsHub /></Lazy></RequireAdminConsole>}
        />
        {/* De vroegere tabbladen, elk op een eigen adres. Statisch, dus `settings/catalogs` wint niet van
            `settings/catalogs/:id` en omgekeerd. */}
        {SETTINGS_MODULES.map((module) => (
          <Route
            key={module}
            path={`settings/${module}`}
            element={<RequireAdminConsole permission={settingsModuleAtom(module)}><Lazy><TenantSettingsModule module={module} /></Lazy></RequireAdminConsole>}
          />
        ))}
        {/* Eén catalogus inrichten (issue 135) — een eigen pagina en geen modal: er zijn te
            veel velden, en de proefweergave hoort ernaast te passen. `new` is statisch en wint
            van `:id`, zodat een nieuw adres nooit als id gelezen wordt. */}
        <Route
          path="settings/catalogs/new"
          element={<RequireAdminConsole permission="tenant_config.configure"><Lazy><TenantCatalogEditor /></Lazy></RequireAdminConsole>}
        />
        <Route
          path="settings/catalogs/:id"
          element={<RequireAdminConsole permission="tenant_config.view"><Lazy><TenantCatalogEditor /></Lazy></RequireAdminConsole>}
        />
        {/* Oud `/catalog-settings` blijft werken als redirect naar de catalogusinstellingen — geen dode link. */}
        <Route path="catalog-settings" element={<Navigate to="/settings/catalog" replace />} />
        <Route
          path="locations"
          element={<RequireAdminConsole permission="location.manage"><Lazy><TenantLocations /></Lazy></RequireAdminConsole>}
        />
        {/* LTI-aanbieders (issue 448). Achter `integration.manage` — het recht
            gaat over koppelingen beheren, niet over LTI, zodat een tweede
            koppel-soort er straks aan hangt in plaats van een eigen atoom te
            krijgen. */}
        <Route
          path="lti-providers"
          element={<RequireAdminConsole permission="integration.manage"><Lazy><TenantLtiProviders /></Lazy></RequireAdminConsole>}
        />
        <Route
          path="external-people"
          element={<RequireAdminConsole permission="external_person.manage"><Lazy><TenantExternalPeople /></Lazy></RequireAdminConsole>}
        />
        <Route
          path="external-companies"
          element={<RequireAdminConsole permission="external_company.manage"><Lazy><TenantExternalCompanies /></Lazy></RequireAdminConsole>}
        />
        <Route
          path="external-request-types"
          element={<RequireAdminConsole permission="external_request_type.view"><Lazy><TenantExternalRequestTypes /></Lazy></RequireAdminConsole>}
        />
        <Route
          path="external-request-types/:typeId"
          element={<RequireAdminConsole permission="external_request_type.view"><Lazy><TenantExternalRequestType /></Lazy></RequireAdminConsole>}
        />
        <Route
          path="external-request-type-versions/:versionId"
          element={<RequireAdminConsole permission="external_request_type.view"><Lazy><TenantExternalRequestVersionEditor /></Lazy></RequireAdminConsole>}
        />
        <Route
          path="workflows"
          element={<RequireAdminConsole permission="workflow.view"><Lazy><TenantWorkflows /></Lazy></RequireAdminConsole>}
        />
        <Route
          path="workflows/help"
          element={<RequireAdminConsole permission="workflow.view"><Lazy><WorkflowHelp /></Lazy></RequireAdminConsole>}
        />
        <Route
          path="workflows/:id"
          element={<RequireAdminConsole permission="workflow.view"><Lazy><TenantWorkflowEditor /></Lazy></RequireAdminConsole>}
        />
        {/* Goedkeuringsflows (issue 271): hoort onder Workflows. RequireAdminConsole
            houdt het op de manage-host; de rechten (view/edit/delete) worden door de
            API afgedwongen, want ze zijn te delegeren aan niet-admins. */}
        <Route
          path="approval-flows"
          element={<RequireAdminConsole permission="approval_flow.view"><Lazy><TenantApprovalFlows /></Lazy></RequireAdminConsole>}
        />
        <Route
          path="approval-flows/:id"
          element={<RequireAdminConsole permission="approval_flow.view"><Lazy><TenantApprovalFlowEditor /></Lazy></RequireAdminConsole>}
        />
        {/* Vragenlijsten: vragen, categorieën, formulieren en losse vragenlijsten, elk een tab met een eigen adres.
            `/questionnaires` wijst naar de vragen. `new` staat vóór `:id`. */}
        <Route
          path="questionnaires"
          element={<RequireAdminConsole permission="questionnaire.view"><Navigate to="/questionnaires/questions" replace /></RequireAdminConsole>}
        />
        <Route
          path="questionnaires/questions"
          element={<RequireAdminConsole permission="questionnaire.view"><Lazy><TenantQuestions /></Lazy></RequireAdminConsole>}
        />
        <Route
          path="questionnaires/questions/new"
          element={<RequireAdminConsole permission="questionnaire.manage"><Lazy><TenantQuestionEditor /></Lazy></RequireAdminConsole>}
        />
        <Route
          path="questionnaires/questions/:id"
          element={<RequireAdminConsole permission="questionnaire.view"><Lazy><TenantQuestionEditor /></Lazy></RequireAdminConsole>}
        />
        <Route
          path="questionnaires/categories"
          element={<RequireAdminConsole permission="questionnaire.view"><Lazy><TenantQuestionCategories /></Lazy></RequireAdminConsole>}
        />
        <Route
          path="questionnaires/categories/new"
          element={<RequireAdminConsole permission="questionnaire.manage"><Lazy><TenantQuestionCategoryEditor /></Lazy></RequireAdminConsole>}
        />
        <Route
          path="questionnaires/categories/:id"
          element={<RequireAdminConsole permission="questionnaire.view"><Lazy><TenantQuestionCategoryEditor /></Lazy></RequireAdminConsole>}
        />
        {/* De formulieren. Bekijken op `questionnaire.view` (alleen-lezen zonder `manage`); een nieuw
            formulier op `questionnaire.manage`. Pauzeren en hervatten vraagt `questionnaire.assign`,
            maar dat zit op de knoppen in het scherm, niet op de route. */}
        <Route
          path="questionnaires/forms"
          element={<RequireAdminConsole permission="questionnaire.view"><Lazy><TenantQuestionnaireForms /></Lazy></RequireAdminConsole>}
        />
        <Route
          path="questionnaires/forms/new"
          element={<RequireAdminConsole permission="questionnaire.manage"><Lazy><TenantQuestionnaireFormEditor /></Lazy></RequireAdminConsole>}
        />
        <Route
          path="questionnaires/forms/:id"
          element={<RequireAdminConsole permission="questionnaire.view"><Lazy><TenantQuestionnaireFormEditor /></Lazy></RequireAdminConsole>}
        />
        {/* Losse vragenlijsten: de lijst lezen op `questionnaire.view`, net als de server. Een nieuwe
            maken en bewerken vóór het versturen vraagt `survey.send` — niet `questionnaire.assign`:
            versturen naar een doelgroep is een eigen, gevaarlijk recht (D9). `new` staat vóór `:id`. */}
        <Route
          path="questionnaires/surveys"
          element={<RequireAdminConsole permission="questionnaire.view"><Lazy><TenantSurveys /></Lazy></RequireAdminConsole>}
        />
        <Route
          path="questionnaires/surveys/new"
          element={<RequireAdminConsole permission="survey.send"><Lazy><TenantSurveyEditor /></Lazy></RequireAdminConsole>}
        />
        <Route
          path="questionnaires/surveys/:id"
          element={<RequireAdminConsole permission="survey.send"><Lazy><TenantSurveyEditor /></Lazy></RequireAdminConsole>}
        />
        <Route
          path="questionnaires/surveys/:id/recipients"
          element={<RequireAdminConsole permission="questionnaire.view"><Lazy><TenantSurveyRecipients /></Lazy></RequireAdminConsole>}
        />
        {/* Alle toewijzingen in één lijst (de vijfde tab): lezen op `questionnaire.view`; pauzeren
            en hervatten vraagt `questionnaire.assign` en zit op de knoppen. Een vast segment naast
            `:assignmentId` hieronder, dus geen botsing. */}
        <Route
          path="questionnaires/assignments"
          element={<RequireAdminConsole permission="questionnaire.view"><Lazy><TenantAssignmentsOverview /></Lazy></RequireAdminConsole>}
        />
        {/* Hoe de uitnodigingen van één toewijzing ervoor staan (bouwplan §2.8). Lezen op
            `questionnaire.view`; herinneren en opnieuw sturen vraagt `questionnaire.assign`, en dat
            zit op de knoppen in het scherm, niet op de route. */}
        <Route
          path="questionnaires/assignments/:assignmentId"
          element={<RequireAdminConsole permission="questionnaire.view"><Lazy><TenantAssignmentStatus /></Lazy></RequireAdminConsole>}
        />
        {/* Niveau 3: één toewijzing (de regel voor één uitvoering). Hetzelfde scherm, beperkt tot die
            uitvoering; dezelfde poort. */}
        <Route
          path="questionnaires/assignments/:assignmentId/occurrences/:occurrenceId"
          element={<RequireAdminConsole permission="questionnaire.view"><Lazy><TenantAssignmentStatus /></Lazy></RequireAdminConsole>}
        />
        {/* Toetsen: dezelfde module als de vragenlijsten, op het toetsoppervlak, met eigen rechten
            (`test.view`, `test.manage`). Tabs met een eigen adres, `/tests` wijst naar de formulieren,
            en er is geen tab voor losse vragenlijsten: een toets gaat altijd via een regel naar een
            training. `new` staat vóór `:id`. Pauzeren en hervatten vraagt `test.assign` en zit op
            de knoppen, niet op de route. */}
        <Route
          path="tests"
          element={<RequireAdminConsole permission="test.view"><Navigate to="/tests/forms" replace /></RequireAdminConsole>}
        />
        <Route
          path="tests/forms"
          element={<RequireAdminConsole permission="test.view"><TestSurface><Lazy><TenantQuestionnaireForms /></Lazy></TestSurface></RequireAdminConsole>}
        />
        <Route
          path="tests/forms/new"
          element={<RequireAdminConsole permission="test.manage"><TestSurface><Lazy><TenantQuestionnaireFormEditor /></Lazy></TestSurface></RequireAdminConsole>}
        />
        <Route
          path="tests/forms/:id"
          element={<RequireAdminConsole permission="test.view"><TestSurface><Lazy><TenantQuestionnaireFormEditor /></Lazy></TestSurface></RequireAdminConsole>}
        />
        <Route
          path="tests/questions"
          element={<RequireAdminConsole permission="test.view"><TestSurface><Lazy><TenantQuestions /></Lazy></TestSurface></RequireAdminConsole>}
        />
        <Route
          path="tests/questions/new"
          element={<RequireAdminConsole permission="test.manage"><TestSurface><Lazy><TenantQuestionEditor /></Lazy></TestSurface></RequireAdminConsole>}
        />
        <Route
          path="tests/questions/:id"
          element={<RequireAdminConsole permission="test.view"><TestSurface><Lazy><TenantQuestionEditor /></Lazy></TestSurface></RequireAdminConsole>}
        />
        <Route
          path="tests/categories"
          element={<RequireAdminConsole permission="test.view"><TestSurface><Lazy><TenantQuestionCategories /></Lazy></TestSurface></RequireAdminConsole>}
        />
        <Route
          path="tests/categories/new"
          element={<RequireAdminConsole permission="test.manage"><TestSurface><Lazy><TenantQuestionCategoryEditor /></Lazy></TestSurface></RequireAdminConsole>}
        />
        <Route
          path="tests/categories/:id"
          element={<RequireAdminConsole permission="test.view"><TestSurface><Lazy><TenantQuestionCategoryEditor /></Lazy></TestSurface></RequireAdminConsole>}
        />
        <Route
          path="tests/assignments"
          element={<RequireAdminConsole permission="test.view"><TestSurface><Lazy><TenantAssignmentsOverview /></Lazy></TestSurface></RequireAdminConsole>}
        />
        <Route
          path="tests/assignments/:assignmentId"
          element={<RequireAdminConsole permission="test.view"><TestSurface><Lazy><TenantAssignmentStatus /></Lazy></TestSurface></RequireAdminConsole>}
        />
        <Route
          path="tests/assignments/:assignmentId/occurrences/:occurrenceId"
          element={<RequireAdminConsole permission="test.view"><TestSurface><Lazy><TenantAssignmentStatus /></Lazy></TestSurface></RequireAdminConsole>}
        />
        {/* Logboek (issue 281): op de manage-host, met de rechten door de API
            afgedwongen. Inzage is bewust te delegeren aan een auditor die geen
            beheerder is — R-005 — dus het recht zit op de route en niet op
            "is admin". */}
        <Route
          path="audit-log"
          element={<RequireAdminConsole permission="audit.view"><Lazy><TenantAuditLog /></Lazy></RequireAdminConsole>}
        />
        {/* De mediabibliotheek (issue 1237): achter `asset.manage` én op de beheer-host.
            Verwijderen raakt elke activiteit die het beeld gebruikt, dus dit is geen scherm
            voor "elke ingelogde gebruiker". */}
        <Route
          path="media"
          element={<RequireAdminConsole permission="asset.manage"><Lazy><TenantMediaLibrary /></Lazy></RequireAdminConsole>}
        />
        {/* Goedkeuringen-inbox: voor elke authed tenant-gebruiker (approver is
            niet per se admin), dus alleen RequireAuth (via de parent-outlet). */}
        <Route path="approvals" element={<Lazy><TenantApprovals /></Lazy>} />
        {/* Het beheer-overzicht (issue 912): achter `approval.handle` én op de
            beheer-host, want het toont de taken van de hele omgeving. De inbox
            hierboven blijft bewust open voor elke ingelogde gebruiker. */}
        <Route
          path="open-approvals"
          element={<RequireAdminConsole permission="approval.handle"><Lazy><TenantApprovalsOverview /></Lazy></RequireAdminConsole>}
        />
        <Route path="profile" element={<Lazy><ProfileView /></Lazy>} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Route>
    </Routes>
  );
}
